import { useCallback } from 'react';
import { useLocalData } from './useLocalData';
import type { PriceBulk, PriceRecord, Price } from '../types/price';
import { generateUniqueRefId } from '../utils/refIdGenerator';

export interface UsePriceDataOptions {
  autoSync?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface UsePriceDataReturn {
  // Price Records
  priceRecords: PriceRecord[];
  priceRecordsLoading: boolean;
  priceRecordsError: string | null;
  totalPriceRecords: number;
  currentPage: number;

  // Individual Prices
  prices: Price[];
  pricesLoading: boolean;
  pricesError: string | null;
  totalPrices: number;

  // Actions
  createPriceRecord: (data: Partial<PriceRecord>) => Promise<PriceRecord>;
  updatePriceRecord: (id: string, data: Partial<PriceRecord>) => Promise<PriceRecord>;
  deletePriceRecord: (id: string) => Promise<void>;

  createPrice: (data: Partial<Price>) => Promise<Price>;
  updatePrice: (id: string, data: Partial<Price>) => Promise<Price>;
  deletePrice: (id: string) => Promise<void>;

  // Bulk operations
  createBulkPrice: (data: PriceBulk) => Promise<{ priceRecord: PriceRecord; prices: Price[] }>;

  // Sync operations
  sync: () => Promise<void>;
  syncStatus: any;
  refresh: () => Promise<void>;

  // Local data operations
  updateLocalData: (updater: (data: any[]) => any[]) => void;
}

export const usePriceData = (options: UsePriceDataOptions = {}): UsePriceDataReturn => {
  const {
    autoSync = true,
    sortBy = 'createTime',
    sortOrder = 'desc',
    page = 1,
    limit = 10,
  } = options;

  // Price Records
  const {
    data: priceRecords,
    loading: priceRecordsLoading,
    error: priceRecordsError,
    create: createPriceRecordLocal,
    update: updatePriceRecordLocal,
    delete: deletePriceRecordLocal,
    refresh: refreshPriceRecords,
    sync: syncPriceRecords,
    syncStatus: priceRecordsSyncStatus,
    total: totalPriceRecords,
    currentPage,
  } = useLocalData({
    entity: 'priceRecord',
    autoSync,
    sortBy,
    sortOrder,
    page,
    limit,
  });

  // Individual Prices (now using priceRecord for all operations)
  const {
    data: prices,
    loading: pricesLoading,
    error: pricesError,
    create: createPriceLocal,
    update: updatePriceLocal,
    delete: deletePriceLocal,
    refresh: refreshPrices,
    sync: syncPrices,
    syncStatus: pricesSyncStatus,
    total: totalPrices,
    updateLocalData,
  } = useLocalData({
    entity: 'priceRecord',
    autoSync,
    sortBy,
    sortOrder,
    page,
    limit,
  });

  // Create Price Record
  const createPriceRecord = useCallback(
    async (data: Partial<PriceRecord>): Promise<PriceRecord> => {
      const refId = generateUniqueRefId('price');
      const priceRecordData = {
        ...data,
        refId,
        createTime: Date.now(),
        updateTime: Date.now(),
        isActive: true,
      };
      await createPriceRecordLocal(priceRecordData);
      return priceRecordData as PriceRecord;
    },
    [createPriceRecordLocal]
  );

  // Update Price Record
  const updatePriceRecord = useCallback(
    async (id: string, data: Partial<PriceRecord>): Promise<PriceRecord> => {
      const updatedData = {
        ...data,
        updateTime: Date.now(),
      };

      await updatePriceRecordLocal(id, updatedData);
      return updatedData as PriceRecord;
    },
    [updatePriceRecordLocal]
  );

  // Delete Price Record
  const deletePriceRecord = useCallback(
    async (id: string): Promise<void> => {
      await deletePriceRecordLocal(id);
    },
    [deletePriceRecordLocal]
  );

  // Create Individual Price (now creates a price record)
  const createPrice = useCallback(
    async (data: Partial<Price>): Promise<Price> => {
      const refId = generateUniqueRefId('price');
      const priceRecordData = {
        ...data,
        refId,
        createTime: Date.now(),
        updateTime: Date.now(),
        isActive: true,
        // Convert individual price data to price record format
        productData: {
          [data.productRefId || 'default']: {
            productId: data.productRefId,
            price: data.price,
            productRemarks: data.productRemarks,
            unit: data.unit,
            wasInSuggestedPresentation: data.wasInSuggestedPresentation || 'no',
            wasUnderSuggestedBrand: data.wasUnderSuggestedBrand || 'no',
            altPresentation: data.altPresentation,
            photoOrInvoice: data.photoOrInvoice,
          },
        },
      };
      await createPriceLocal(priceRecordData);
      return priceRecordData as any as Price; // Cast for compatibility
    },
    [createPriceLocal]
  );

  // Update Individual Price (now updates a price record)
  const updatePrice = useCallback(
    async (id: string, data: Partial<Price>): Promise<Price> => {
      const updatedData = {
        ...data,
        updateTime: Date.now(),
      };

      await updatePriceLocal(id, updatedData);
      return updatedData as any as Price; // Cast for compatibility
    },
    [updatePriceLocal]
  );

  // Delete Individual Price
  const deletePrice = useCallback(
    async (id: string): Promise<void> => {
      await deletePriceLocal(id);
    },
    [deletePriceLocal]
  );

  // Create Bulk Price (Price Record + Multiple Prices)
  const createBulkPrice = useCallback(
    async (data: PriceBulk): Promise<{ priceRecord: PriceRecord; prices: Price[] }> => {
      try {
        // Create the price record with all the product data included
        const priceRecordData = {
          date: data.date,
          establishmentId: data.establishmentId,
          respondent: data.respondent,
          signature: data.signature,
          signatureInitial: data.signatureInitial,
          finalRemarks: data.finalRemarks,
          refId: data.refId,
          productData: data.productData, // Include product data in the price record
        };

        const priceRecord = await createPriceRecord(priceRecordData);

        // For now, return the price record as both priceRecord and prices
        // since we're using priceRecord for all operations
        return {
          priceRecord,
          prices: [priceRecord], // Return price record as prices for compatibility
        };
      } catch (error) {
        console.error('Error creating bulk price:', error);
        throw error;
      }
    },
    [createPriceRecord]
  );

  // Sync all price data (now only price records)
  const sync = useCallback(async (): Promise<void> => {
    await syncPriceRecords();
  }, [syncPriceRecords]);

  // Refresh all price data (now only price records)
  const refresh = useCallback(async (): Promise<void> => {
    await refreshPriceRecords();
  }, [refreshPriceRecords]);

  return {
    // Price Records
    priceRecords,
    priceRecordsLoading,
    priceRecordsError,
    totalPriceRecords,
    currentPage,

    // Individual Prices (now using priceRecord for all operations)
    prices,
    pricesLoading,
    pricesError,
    totalPrices,

    // Actions
    createPriceRecord,
    updatePriceRecord,
    deletePriceRecord,

    createPrice,
    updatePrice,
    deletePrice,

    // Bulk operations
    createBulkPrice,

    // Sync operations
    sync,
    syncStatus: { priceRecords: priceRecordsSyncStatus, prices: priceRecordsSyncStatus },
    refresh,

    // Local data operations
    updateLocalData,
  };
};
